import { POSTS_VIEW_OPTIONS, ALL_REGIONS_TAG_ID } from '../../util/constants.js'
import { mapTagsToVueFormulateObjects, mapAdmin } from '../util.js'
import { toPascalCase, capitalizeFirstLetter } from '../../util/helper.js'

// GLOBAL
export function isLoading(state) {
  return state.isLoading
}

// SETTINGS
export function getSettings(state) {
  return state.settings
}
export function getAdmins(state) {
  return state.settings?.admins.map((admin) => mapAdmin(admin)) || []
}
export function getAdminsPublic(_state, getters) {
  return getters.getAdmins.filter((admin) => admin.enable_public_contact)
}
export const getAdmin = (_state, getters) => (id) => {
  return getters.getAdmins.find((admin) => admin.id === id) || null
}
export function getTenantId(state) {
  return state.settings?.tenant_id
}
export function getMenu(state) {
  return state.settings?.menu
}
export function getTagsCategories(state) {
  return state.settings?.tag_categories
}
export function getTagsCategoriesByCategoryId(_state, getters) {
  // Example:
  // {
  //   6: 'school_support',
  //   3: 'industries'
  // }
  const categories = getters.getTagsCategories
  return Object.entries(categories).reduce((acc, [key, value]) => {
    acc[value] = key
    return acc
  }, {})
}
export function getTagsCategoriesByIdMappedForVueFormulateReports(
  _state,
  getters,
) {
  // NOTE: Filters out general tags, regions, industries, and school support
  // Example:
  // {
  //   6: 'School support',
  //   3: 'Industries'
  // }
  const BLACKLIST = [
    'tags',
    'regions',
    'industries',
    'school_support',
    'organisations',
  ]
  const categories = getters.getTagsCategoriesByCategoryId
  return Object.entries(categories).reduce((categoriesMapped, [key, value]) => {
    if (!BLACKLIST.includes(value)) {
      if (value === 'years') {
        value = 'Year groups'
      }

      categoriesMapped[key] = capitalizeFirstLetter(value.replace(/_/g, ' '))
    }
    return categoriesMapped
  }, {})
}
export const getTagsCategoryKey = (_state, getters) => (tagType) => {
  const category = getters.getTagsCategories[tagType]
  return `category_id_${category}`
}
export const getTagsAllMappedByCategoryId = (_state, getters) => {
  // Example:
  // {
  //   0: [{tag1}, {tag2}],
  //   1: [{tag4}, {tag5}]
  // }
  const key = 'category'
  return getters.getTagsAll.reduce((acc, tag) => {
    const keyName = tag[key]

    if (!acc[keyName]) {
      acc[keyName] = []
    }
    acc[keyName].push(tag)
    return acc
  }, {})
}
export const getTagsMappedByCategory = (_state, getters) => (tags) => {
  // Example:
  // {
  //   industries: [{tag1}, {tag2}],
  //   school_support: [{tag4}, {tag5}]
  // }
  const key = 'category'
  return tags.reduce((acc, tag) => {
    const keyName = getters.getTagsCategoriesByCategoryId[tag[key]]

    if (!acc[keyName]) {
      acc[keyName] = []
    }
    acc[keyName].push(tag)
    return acc
  }, {})
}
export function getTagsAll(state) {
  return state.settings?.tags_all
}
export const getTagTextByTagId = (_state, getters) => (tagId) => {
  return getters.getTagsAll?.find(
    (tag) => parseInt(tag.value) === parseInt(tagId),
  )?.text
}
export function getTagsGeneral(state) {
  return state.settings?.tags
}
export function getTagsRegions(state) {
  return state.settings?.tag_regions
}
export function getTagsRegionsWithoutAllRegions(state) {
  return state.settings?.tag_regions.filter(
    (tag) => tag.value !== ALL_REGIONS_TAG_ID,
  )
}
// Connections
export function isConnectionsFeatureEnabled(state) {
  return state.settings?.enable_connections_feature
}
export function isConnectionsSignupEnabled(state, getters) {
  return (
    getters.isConnectionsFeatureEnabled &&
    state.settings?.enable_connections_signup
  )
}
export function showPublicConnections(state) {
  return state.settings?.show_public_contacts
}
export function getTagsIndustries(state) {
  return state.settings?.tag_industries
}
export function getTagsSchoolSupport(state) {
  return state.settings?.tag_school_support
}
export function getTagsPreferences(state) {
  return state.settings?.tag_preferences
}
export function getTagsSubjects(state) {
  return state.settings?.tag_subjects
}
export function getTagsYears(state) {
  return state.settings?.tag_years
}
export function getTagsSoftSkills(state) {
  return state.settings?.tag_soft_skills
}
export function getTagsTechSkills(state) {
  return state.settings?.tag_tech_skills
}
export function getTagsOrganisations(state) {
  return state.settings?.tag_organisations
}
export const getTagsMappedForVueFormulate =
  (_state, getters) =>
  // @TODO might not need this depending how data is passed
  (tags = null, getter = null) => {
    if (tags) {
      return mapTagsToVueFormulateObjects(tags)
    }

    return mapTagsToVueFormulateObjects(getters[getter])
  }
export function getConnectionsData(state) {
  return state.settings?.contacts_data
}
export function getConnectionsTypes(_state, getters) {
  return getters.getConnectionsData?.contact?.types
}
export function getConnectionsTypesMappedForVueFormulate(_state, getters) {
  return getters.getConnectionsTypes?.map((type) => {
    return {
      value: type,
      label: toPascalCase(type),
    }
  })
}
export function getLogo(state) {
  return state.settings?.logo
}
export function getBannerFooter(state) {
  return state.settings?.banner_footer
}
export function getCloudDomain(state) {
  return state.settings?.cloud_domain
}
export function getCloudURL(state) {
  return state.settings?.cloud_url
}
export function getMigrationsMissing(state) {
  return state.settings?.migrations_status?.missing
}
export function getMigrationsStatus(state) {
  return state.settings?.migrations_status?.required
}

export function getClient(state) {
  return state.client
}

export function getPostCategories(state) {
  return state.settings?.post_categories?.filter(
    // filter out pages
    (category) => category.value !== 0,
  )
}

export function isContributor(state) {
  return state.client === 'client1contributor'
}
export function getUser(state) {
  return state.user
}
export function getUserAccess(state) {
  return state.userAccess
}
export function isSuperAdmin(_state, getters) {
  return !!getters.getUser?.super_admin_user
}
export function isCateRegionalAdmin(_state, getters) {
  return !!getters.getUser?.cate_regional_admin_user
}
export function isContributorAdminConnectionsOnly(_state, getters) {
  return !!getters.getUser?.is_contributor_admin_connections_only
}
export function getTemplateView(state) {
  return state.templateView
}
export function getIdleTimer(state) {
  return state.idleTimer
}
export function getIdleTimerClosed(state) {
  return state.idleTimerClosed
}

// POSTS
export function getCurrentPost(state, getters) {
  const currentPost = state.currentPost
  const settings = getters.getSettings

  // @TODO tidy up tags and all components that depend on this structure!!!!!
  // See improved logic in PostUpdate.vue - move to the store
  const setTags = () => {
    if (currentPost) {
      const general_arr = []
      settings.tags.forEach((i) => {
        if (currentPost.tag_titles?.includes(i.text)) {
          general_arr.push(i.text)
        }
      })
      currentPost.general_tags = general_arr

      const industry_arr = []
      settings.tag_industries.forEach((i) => {
        if (currentPost.tag_titles?.includes(i.text)) {
          industry_arr.push(i.text)
        }
      })
      currentPost.industry_tags = industry_arr

      const year_arr = []
      settings.tag_years.forEach((i) => {
        if (currentPost.tag_titles?.includes(i.text)) {
          year_arr.push(i.text)
        }
      })
      currentPost.year_tag = year_arr

      const region_arr = []
      settings.tag_regions.forEach((i) => {
        if (currentPost.tag_titles?.includes(i.text)) {
          region_arr.push(i.text)
        }
      })

      currentPost.region_tag = region_arr
    }
  }
  setTags()

  return {
    ...currentPost,
    cloud_post: !!currentPost.cloud_post || false,
    shared_to_wp: !!currentPost.shared_to_wp || false,
    status: !!currentPost.status || false,
  }
}
export function getCurrentPostTemplateId(state) {
  return state.currentPostsTemplateId
}
export function getCurrentPostCloudpostId(_state, getters) {
  return getters.getCurrentPost?.cloud_post_id || null
}
export function getCurrentPostCloudpostLink(_state, getters) {
  const cloudDomain = getters.getCloudDomain
  const cloudpostId = getters.getCurrentPostCloudpostId
  return `${cloudDomain}/cloudpost/${cloudpostId}`
}
export function getPostsView(state) {
  return state.postsView
}
export function isPostsViewCalendar(state) {
  return state.postsView === POSTS_VIEW_OPTIONS.CALENDAR
}
export function getFilterPostsBy(state) {
  return state.filterPostsBy
}
export function getFilterPostsByCount(_state, getters) {
  return Object.entries(getters.getFilterPostsBy).reduce(
    (count, [key, value]) => {
      if (key === 'category' || key === 'matchAll') {
        return count
      }

      if (value !== null && (!Array.isArray(value) || value.length > 0)) {
        return (count = count + 1)
      }

      return count
    },
    0,
  )
}
export function getFiltersMatchAllOptions() {
  return [
    { label: 'Any selected filter', value: 1 },
    { label: 'Every selected filter', value: 0 },
  ]
}
export function getFilterResourceBy(state) {
  return state.filterResourceBy
}
